/**
 * The running app's shared objects, set once at boot. Scenes and feature views read it
 * instead of threading services through Phaser's registry.
 */
import type * as Phaser from 'phaser';
import type { RemoteConfig } from '../config/content.js';
import type { DeepLink } from '../services/deeplinks.js';
import type { Services } from '../services/index.js';
import type { DeviceClass } from '../services/prompts.js';
import type { UiBridge } from '../ui/bridge.js';
import type { Art } from './art.js';

export interface Flags {
  e2e: boolean;
  dev: boolean;
  /** test hook: every animation completes instantly */
  skipAnimations: boolean;
}

export interface Session {
  offlineCoins: number;
  offlineHours: number;
  offlineShown: boolean;
  dailyShown: boolean;
  /** SO-05 link that opened this session, handled once the game is up */
  deepLink: DeepLink | null;
  /** once-per-session map checks (inbox deliveries, league results, offers) */
  checked: Set<string>;
  /** dev/e2e `?open=<viewKind>&props=<json>`: a view to open once the map is up */
  devOpen?: { kind: string; props: unknown } | null;
  /**
   * A feature the level-up screen's "Try it" asked for while a city moment (Grand Opening's next
   * city or end of content, a shop finale's "Go to My shop") sent the player somewhere else first:
   * the next map visit opens it after its own popups (MapScene afterShow), then forgets it.
   */
  featureAfter?: string | null;
  /** a purchase failed in this session: the rating sheet is not asked for (services/review.ts) */
  purchaseFailed?: boolean;
}

export interface AppContext {
  services: Services;
  art: Art;
  ui: UiBridge;
  flags: Flags;
  game: Phaser.Game | null;
  city: string;
  /** remote-config defaults merged with /v1/config when reachable */
  readonly remote: RemoteConfig;
  session: Session;
  device: DeviceClass;
  /** apply settings changes (sound, language, colour-blind…) from any view */
  changeSettings: (patch: Partial<Services['store']['state']['settings']>) => void;
}

let ctx: AppContext | null = null;

export function setApp(c: AppContext): void {
  ctx = c;
}

export function app(): AppContext {
  if (!ctx) throw new Error('app context not initialised');
  return ctx;
}
